import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Divider,
  Typography,
  Box,
  Paper,
  IconButton,
  TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Edit } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowBack } from "@mui/icons-material";
import classnames from "classnames";
import { enterKeyCode } from "../../constants";

const useStyles = makeStyles(theme => ({
  "@global": {
    '[class*="MuiTabs-root"]': {
      borderTop: "none"
    }
  },
  title: {
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  root: {
    display: "flex",
    position: "relative",
    padding: theme.spacing(2.5, 2, 6, 2),
    flex: 1,
    margin: theme.spacing(3, 3, 1, 3),
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(1.5),
      padding: theme.spacing(3, 0, 5, 0)
    }
  },
  container: {
    display: "flex",
    flexDirection: "column"
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
}));

const useTitleStyles = makeStyles(theme => ({
  titleInput: {
    ...theme.components.MuiTypography.styleOverrides.h5
  },
  editIcon: {
    margin: theme.spacing(-0.5, 0, 0, 1)
  }
}));

const AdminPageLayoutTitle = ({
  editableTitle,
  title,
  onTitleChange,
  titleChangeError,
  onGoBackClick
}) => {
  const classes = useTitleStyles();
  const [editTitleMode, setEditTitleMode] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const inputRef = useRef(null);
  const handleEditTitleClick = () => {
    setEditTitleMode(!editTitleMode);
  };

  useEffect(() => {
    if (editTitleMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editTitleMode]);

  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  const handleTitleChange = () => {
    if (onTitleChange) {
      onTitleChange(titleValue);
    }
    setEditTitleMode(false);
  };
  return (
    <Box display="flex" alignItems="center" mb={2} mt={2}>
      {onGoBackClick && (
        <Box ml={-1.5} mr={1}>
          <IconButton onClick={onGoBackClick} size="large">
            <ArrowBack />
          </IconButton>
        </Box>
      )}
      {editTitleMode ? (
        <TextField
          value={titleValue}
          InputProps={{
            disableUnderline: true,
            className: classes.titleInput,
            onBlur: handleTitleChange,
            onKeyDown: event =>
              event.keyCode === enterKeyCode && handleTitleChange() // submits when enter is pressed
          }}
          onChange={e => setTitleValue(e.target.value)}
          error={!!titleChangeError}
          helperText={titleChangeError}
          inputRef={inputRef}
        />
      ) : (
        <Typography
          variant="h5"
          className={classes.title}
          color={!!titleChangeError ? "error" : "textPrimary"}
        >
          {title}
        </Typography>
      )}
      {editableTitle && (
        <IconButton
          onClick={handleEditTitleClick}
          className={classes.editIcon}
          size="large"
        >
          <Edit color="disabled" />
        </IconButton>
      )}
    </Box>
  );
};

function AdminPageLayout(props) {
  const {
    title,
    headerActions,
    children,
    onCloseIconClick,
    editableTitle,
    onTitleChange,
    titleChangeError,
    onGoBackClick,
    hideDivider,
    className
  } = props;
  const classes = useStyles();
  return (
    <Paper className={classnames(classes.root, className)}>
      <Container maxWidth="xl" className={classes.container}>
        {!!title && (
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            pb={"1px"}
          >
            <AdminPageLayoutTitle
              title={title}
              editableTitle={editableTitle}
              titleChangeError={titleChangeError}
              onTitleChange={onTitleChange}
              onGoBackClick={onGoBackClick}
            />
            {!!headerActions && <Box mb={2}>{headerActions}</Box>}
          </Box>
        )}
        {onCloseIconClick ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onCloseIconClick}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        ) : null}
        {!hideDivider && <Divider />}
        {children}
      </Container>
    </Paper>
  );
}

export default AdminPageLayout;
